import { useState } from 'react'
import { useLabelSettings } from '@/features/orders/hooks/useLabelSettings'
import { canPrint, loadLogoHex } from '@/features/orders/hooks/usePrintOrder'
import { toDantSuMarkup } from '@/features/orders/utils/receiptRenderers'
import { DEFAULT_LABEL_SETTINGS } from '@/features/orders/utils/receiptTypes'
import { trackPrint, type PrintJob } from '@/lib/deviceDiagnostics'
import { cashReportElements, type CashReportLine } from './cashClosingFormatter'

/** Prints report and receipt lines on the bon printer. `logo` adds the restaurant icon when the receipt settings ask for it. */
export function usePrintCashClosing(restaurantId: number) {
  const { data } = useLabelSettings(restaurantId)
  const [isPrinting, setIsPrinting] = useState(false)
  const settings = data ?? DEFAULT_LABEL_SETTINGS
  async function print(lines: CashReportLine[], language: string, { job = 'cash_closing', logo = false }: { job?: PrintJob; logo?: boolean } = {}) {
    if (!canPrint) throw new Error('Printing unavailable')
    setIsPrinting(true)
    try {
      await trackPrint(job, { copies: 1 }, async () => {
        const hex = logo && settings.printLogo ? await loadLogoHex(restaurantId, settings) : undefined
        const elements = cashReportElements(lines, language, settings.printerNbrCharactersPerLine)
        const { EscPosPrinter } = await import('@/plugins/escpos-printer')
        await EscPosPrinter.printFormattedText({
          deviceAddress: localStorage.getItem('printer_device_address') ?? undefined,
          textChunks: [toDantSuMarkup(hex ? [{ type: 'image', hex }, ...elements] : elements, settings.printerNbrCharactersPerLine)],
          printerDpi: settings.printerDpi, printerWidthMM: settings.printerWidthMm,
          printerNbrCharactersPerLine: settings.printerNbrCharactersPerLine,
          printerCharsetName: settings.printerCharsetName, printerCharsetCommand: settings.printerCharsetCommand, copies: 1,
        })
      })
    } finally { setIsPrinting(false) }
  }
  return { canPrint, isPrinting, print }
}
